/**
 * Glossar des Mandanten: alle Begriffe und Abkürzungen der eigenen Richtlinien, je Begriff die
 * festgelegte Definition und jedes Vorkommen mit Abgleich (gleich, umformuliert, abweichend).
 * Geschäftsstelle und Administration legen Definitionen fest — aus einem Vorkommen oder frei.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, BookMarked, Check, Equal, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import { GUIDELINES_PATH } from "./guidelinesPath";
import { useGuidelinesAPI, type GlossaryEntry, type GlossaryOccurrenceState, type GlossaryView } from "./GuidelinesAPI";

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Status nie nur über Farbe: Symbol und Wort dazu. */
function StateBadge({ state }: { state: GlossaryOccurrenceState | null }) {
  const { t } = useTranslation();
  if (!state) return null;
  const Icon = state === "different" ? AlertTriangle : state === "same" ? Check : Equal;
  return (
    <Badge variant={state === "different" ? "destructive" : "secondary"} className="shrink-0 gap-1 px-1.5 py-0 text-[11px] font-normal">
      <Icon size={11} aria-hidden />
      {t(`guidelinesPage.glossary.state.${state}`)}
    </Badge>
  );
}

function EntryCard({ entry, canEdit, onSave, onRemove }: { entry: GlossaryEntry; canEdit: boolean; onSave: (definition: string) => Promise<void>; onRemove: () => Promise<void> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(entry.glossary?.definition ?? entry.reference ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (definition: string) => {
    setBusy(true);
    try {
      await onSave(definition);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg border bg-card p-4" data-glossary={entry.key}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{entry.term}</span>
        {entry.kind === "abbreviation" ? <Badge variant="outline" className="px-1.5 py-0 text-[11px] font-normal">{t("guidelinesPage.glossary.abbreviation")}</Badge> : null}
        {entry.state === "different" ? (
          <Badge variant="destructive" className="gap-1 px-1.5 py-0 text-[11px] font-normal"><AlertTriangle size={11} aria-hidden />{t("guidelinesPage.glossary.entryDifferent")}</Badge>
        ) : null}
        <span className="flex-1" />
        {canEdit && !editing ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Pencil size={13} className="mr-1" />
              {entry.glossary ? t("guidelinesPage.glossary.edit") : t("guidelinesPage.glossary.define")}
            </Button>
            {entry.glossary ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onRemove()} aria-label={t("guidelinesPage.glossary.remove")}>
                <Trash2 size={13} />
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="mt-2 text-sm">
        {editing ? (
          <div className="flex flex-col gap-2">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="glossary-definition" />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy || !text.trim()} onClick={() => void save(text)}>
                <Check size={13} className="mr-1" />
                {t("guidelinesPage.glossary.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                <X size={13} className="mr-1" />
                {t("guidelinesPage.cancel")}
              </Button>
            </div>
          </div>
        ) : entry.glossary ? (
          <p className="flex gap-2">
            <BookMarked size={14} className="mt-0.5 shrink-0 text-muted-foreground" aria-label={t("guidelinesPage.glossary.inGlossary")} />
            <span>{entry.glossary.definition}</span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{t("guidelinesPage.glossary.notDefined")}</p>
        )}
      </div>

      <ul className="mt-3 flex flex-col divide-y border-t">
        {entry.occurrences.map((o) => (
          <li key={`${o.documentId}:${o.sectionId}:${o.term}`} className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-start sm:gap-3">
            <button
              type="button"
              className="shrink-0 text-left text-xs font-medium text-primary hover:underline sm:w-40"
              onClick={() => navigate(`${GUIDELINES_PATH}/${o.documentId}${o.number ? `?section=${encodeURIComponent(o.number)}` : ""}`)}
            >
              {o.guideline} {o.number ?? ""}
              {o.draft ? <span className="ml-1 font-normal text-muted-foreground">({t("guidelinesPage.glossary.draft")})</span> : null}
            </button>
            <span className={cn("min-w-0 flex-1", o.state === "different" ? "" : "text-muted-foreground")}>
              {o.qualifier ? <span className="mr-1 text-xs text-muted-foreground">({o.qualifier})</span> : null}
              {o.definition}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              {entry.glossary || entry.occurrences.length > 1 ? <StateBadge state={o.state} /> : null}
              {/* Übernehmen: was noch nicht der Festlegung entspricht, oder als erste Festlegung. */}
              {canEdit && (!entry.glossary || o.state !== "same") ? (
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy} onClick={() => void save(o.definition)}>
                  {t("guidelinesPage.glossary.adopt")}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </li>
  );
}

export function GlossaryPanel({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(customer);
  const [view, setView] = useState<GlossaryView | null>(null);
  const [filter, setFilter] = useState("");
  const [show, setShow] = useState<"all" | "different" | "defined" | "undefined">("all");

  const load = useCallback(() => {
    api.getGlossary().then(setView).catch((e) => toast.error(errorText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);
  useEffect(load, [load]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const keep = (e: GlossaryEntry) => (show === "different" ? e.state === "different" : show === "defined" ? !!e.glossary : show === "undefined" ? !e.glossary : true);
    return (view?.entries ?? []).filter((e) => keep(e) && (!q || e.term.toLowerCase().includes(q) || e.occurrences.some((o) => o.definition.toLowerCase().includes(q))));
  }, [view, filter, show]);

  if (!view) return null;
  const save = async (entry: GlossaryEntry, definition: string) => {
    try {
      await api.setGlossaryTerm({ term: entry.term, definition, kind: entry.kind });
      toast.success(t("guidelinesPage.glossary.saved", { term: entry.term }));
      load();
    } catch (e) {
      toast.error(errorText(e));
      throw e;
    }
  };
  const remove = async (entry: GlossaryEntry) => {
    try {
      await api.deleteGlossaryTerm(entry.key);
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="glossary-panel">
      <p className="text-sm text-muted-foreground">
        {t("guidelinesPage.glossary.summary", { total: view.counts.total, defined: view.counts.defined, different: view.counts.different })}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Input className="max-w-xs" placeholder={t("guidelinesPage.glossary.filter")} value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="flex flex-wrap gap-1" role="group" aria-label={t("guidelinesPage.glossary.show")}>
          {(["all", "different", "defined", "undefined"] as const).map((k) => (
            <Button key={k} size="sm" variant={show === k ? "default" : "outline"} className="h-8" onClick={() => setShow(k)} data-show={k}>
              {t(`guidelinesPage.glossary.filterBy.${k}`)}
              <span className="ml-1 tabular-nums opacity-70">
                {k === "all" ? view.counts.total : k === "different" ? view.counts.different : k === "defined" ? view.counts.defined : view.counts.total - view.entries.filter((e) => e.glossary).length}
              </span>
            </Button>
          ))}
        </div>
      </div>
      {shown.length ? (
        <ul className="flex flex-col gap-3">
          {shown.map((e) => (
            <EntryCard key={e.key} entry={e} canEdit={view.canEdit} onSave={(d) => save(e, d)} onRemove={() => remove(e)} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t("guidelinesPage.glossary.empty")}</p>
      )}
    </div>
  );
}
